// =============================================================================
// PEDRA — src/App.tsx
// Casca do aplicativo: providers, layout (cabecalho + entradas + resultado),
// modais, atalhos de teclado, tema, importacao/exportacao e carregamento do
// projeto vindo da URL.
//
// O App e o unico dono do estado de INTERFACE (tema, gaveta, modais, avisos).
// O estado do PROJETO mora em <ProjectProvider>; o do calculo, em
// <OptimizerProvider>.
// =============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';

import type { Id, Part } from './core/types';
import { validateProject } from './core/validation';

import { ProjectProvider, useProject } from './state/store';
import { OptimizerProvider, useOptimizer } from './state/useOptimizer';
import { UI_STORAGE_KEY, consumeMigrationNotice, loadUiPrefs, saveUiPrefs } from './state/persistence';
import type { UiPrefs } from './state/persistence';

import { Header } from './components/Header';
import type { CsvImportTarget, CsvTarget } from './components/Header';
import { Sidebar, isBlankPartRow, isBlankStockRow, projectWithoutBlankRows } from './components/Sidebar';
import { ResultsPanel } from './components/ResultsPanel';
import { SettingsModal } from './components/SettingsModal';
import { EdgeBandingModal } from './components/EdgeBandingModal';
import { Modal } from './components/Modal';

import {
  cutsToCsv,
  downloadText,
  offcutsToCsv,
  parsePartsCsv,
  parseStockCsv,
  partsToCsv,
  stockToCsv,
} from './lib/csv';
import { downloadProject, importProjectJson, readFileAsText } from './lib/projectFile';
import { clearUrlProject, createShareLink, readProjectFromUrl } from './lib/share';
import { printResult } from './lib/printPdf';
import { slugifyFilename } from './lib/format';

// -----------------------------------------------------------------------------
// TEMA
// -----------------------------------------------------------------------------

export type ThemeName = 'light' | 'dark';

/**
 * Tema inicial: o que estiver salvo nas preferencias; sem nada salvo, segue a
 * preferencia do sistema operacional.
 */
export function resolveInitialTheme(): ThemeName {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(UI_STORAGE_KEY) !== null) {
      return loadUiPrefs().theme;
    }
  } catch {
    // Armazenamento bloqueado (modo anonimo restrito): cai na preferencia do SO.
  }
  try {
    if (typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  } catch {
    // Navegador sem matchMedia: tema claro.
  }
  return 'light';
}

/** Aplica o tema no elemento <html>, de onde todas as variaveis descem. */
export function applyTheme(theme: ThemeName): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.style.colorScheme = theme;
}

// -----------------------------------------------------------------------------
// LAYOUT
// -----------------------------------------------------------------------------

/**
 * true quando a coluna de entradas esta em modo GAVETA — ou seja, quando ela
 * cobre o resultado em vez de dividir a tela com ele. O limite e o mesmo do
 * CSS (768px, o unico breakpoint do projeto).
 */
export function isDrawerLayout(): boolean {
  try {
    return typeof matchMedia === 'function' && matchMedia('(max-width: 768px)').matches;
  } catch {
    // Navegador sem matchMedia: assume a tela larga, onde nada fica coberto.
    return false;
  }
}

// -----------------------------------------------------------------------------
// AVISO FLUTUANTE
// -----------------------------------------------------------------------------

interface Toast {
  id: number;
  tone: 'info' | 'success' | 'danger';
  text: string;
}

const TOAST_MS = 5000;

// -----------------------------------------------------------------------------
// MODAL DE ATALHOS
// -----------------------------------------------------------------------------

const SHORTCUTS: ReadonlyArray<{ keys: string; what: string }> = [
  { keys: 'Ctrl+Enter', what: 'Calcular o plano de corte' },
  { keys: 'Esc', what: 'Cancelar o cálculo ou fechar a janela aberta' },
  { keys: 'Ctrl+B', what: 'Mostrar ou ocultar as entradas' },
  { keys: 'Ctrl+S', what: 'Salvar o projeto em arquivo .json' },
  { keys: 'Ctrl+O', what: 'Abrir um projeto .json' },
  { keys: 'Ctrl+Z', what: 'Desfazer' },
  { keys: 'Ctrl+Shift+Z', what: 'Refazer' },
  { keys: 'Ctrl+P', what: 'Imprimir ou salvar em PDF' },
  { keys: 'Shift+?', what: 'Mostrar esta lista' },
  { keys: 'Ctrl+V', what: 'Colar peças copiadas do Excel na tabela' },
  { keys: 'Ctrl+Del', what: 'Remover as linhas selecionadas' },
  { keys: '← →', what: 'Chapa anterior / próxima (com o diagrama focado)' },
];

function ShortcutsModal(props: { open: boolean; onClose(): void }): ReactElement {
  return (
    <Modal open={props.open} title="Atalhos de teclado" onClose={props.onClose} size="md">
      <table className="table table--zebra">
        <thead>
          <tr>
            <th scope="col">Atalho</th>
            <th scope="col">Ação</th>
          </tr>
        </thead>
        <tbody>
          {SHORTCUTS.map((s) => (
            <tr key={s.keys}>
              <td>
                <kbd>{s.keys}</kbd>
              </td>
              <td>{s.what}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// MODAL DE COMPARTILHAMENTO
// -----------------------------------------------------------------------------

interface ShareState {
  url: string;
  error: string | null;
}

interface ShareModalProps {
  open: boolean;
  share: ShareState | null;
  onClose(): void;
  onCopied(ok: boolean): void;
}

function ShareModal(props: ShareModalProps): ReactElement {
  const { open, share, onClose, onCopied } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);

  const copy = useCallback((): void => {
    const input = inputRef.current;
    if (!input) return;
    input.select();
    const text = input.value;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text).then(
        () => onCopied(true),
        () => onCopied(false),
      );
      return;
    }
    onCopied(false);
  }, [onCopied]);

  return (
    <Modal open={open} title="Compartilhar projeto" onClose={onClose} size="md">
      {share === null ? null : share.error !== null ? (
        <div className="notice notice--danger" role="alert">
          {share.error}
        </div>
      ) : (
        <div className="stack">
          <p className="field__help">
            O projeto inteiro (peças, chapas e configurações) viaja dentro do próprio link — nada é
            enviado para servidor algum. Quem abrir o link vê exatamente este projeto.
          </p>
          <div className="row">
            <input
              ref={inputRef}
              className="input truncate"
              type="text"
              readOnly
              value={share.url}
              aria-label="Link do projeto"
              onFocus={(e) => e.currentTarget.select()}
            />
            <button type="button" className="btn btn--primary" onClick={copy}>
              Copiar
            </button>
          </div>
          <p className="field__help">{`${share.url.length} caracteres.`}</p>
        </div>
      )}
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// CASCA (dentro dos providers)
// -----------------------------------------------------------------------------

function AppShell(): ReactElement {
  const { project, dispatch, canUndo, canRedo, saveStatus } = useProject();
  const optimizer = useOptimizer();

  // --- preferencias de interface --------------------------------------------
  const [prefs, setPrefs] = useState<UiPrefs>(() => {
    const loaded = loadUiPrefs();
    return { ...loaded, theme: resolveInitialTheme() };
  });

  const updatePrefs = useCallback((patch: Partial<UiPrefs>): void => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch };
      saveUiPrefs(next);
      return next;
    });
  }, []);

  // Gaveta: aberta por padrao no desktop, fechada no celular.
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => !isDrawerLayout());

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [edgeOpen, setEdgeOpen] = useState(false);
  const [bandingPartId, setBandingPartId] = useState<Id | null>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [share, setShare] = useState<ShareState | null>(null);
  const [hoveredPartId, setHoveredPartId] = useState<Id | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  const toastSeq = useRef(0);
  const projectInputRef = useRef<HTMLInputElement | null>(null);
  const csvInputRef = useRef<HTMLInputElement | null>(null);
  const csvTargetRef = useRef<CsvImportTarget>('parts');

  const notify = useCallback((tone: Toast['tone'], text: string): void => {
    toastSeq.current += 1;
    setToast({ id: toastSeq.current, tone, text });
  }, []);

  // O aviso some sozinho.
  useEffect(() => {
    if (toast === null) return undefined;
    const id = window.setTimeout(() => {
      setToast((cur) => (cur !== null && cur.id === toast.id ? null : cur));
    }, TOAST_MS);
    return () => window.clearTimeout(id);
  }, [toast]);

  // --- tema ------------------------------------------------------------------
  useEffect(() => {
    applyTheme(prefs.theme);
  }, [prefs.theme]);

  const toggleTheme = useCallback((): void => {
    updatePrefs({ theme: prefs.theme === 'dark' ? 'light' : 'dark' });
  }, [prefs.theme, updatePrefs]);

  // --- aviso da migracao do rascunho (v1 -> v2) ------------------------------
  // O carregamento do rascunho acontece no reducer do projeto, que nao mostra
  // avisos. Ele deixa o recado em `consumeMigrationNotice()` e o App o exibe
  // aqui, uma unica vez: a correcao e automatica, mas nunca escondida.
  const migrationNoticeShownRef = useRef(false);
  useEffect(() => {
    if (migrationNoticeShownRef.current) return;
    migrationNoticeShownRef.current = true;
    const text = consumeMigrationNotice();
    if (text !== null) notify('info', text);
  }, [notify]);

  // --- projeto vindo da URL (uma unica vez, antes de qualquer edicao) --------
  const urlLoadedRef = useRef(false);
  useEffect(() => {
    if (urlLoadedRef.current) return;
    urlLoadedRef.current = true;
    const fromUrl = readProjectFromUrl();
    if (fromUrl === null) return;
    dispatch({ type: 'setProject', project: fromUrl });
    clearUrlProject();
    notify('info', 'Projeto carregado a partir do link compartilhado.');
  }, [dispatch, notify]);

  // --- validacao (selo de erros no cabecalho) --------------------------------
  const errorCount = useMemo(
    () => validateProject(projectWithoutBlankRows(project)).errors.length,
    [project],
  );

  // --- calculo ---------------------------------------------------------------
  // A grade mantem uma linha vazia no fim de cada tabela; o otimizador reprova
  // essas linhas. Antes de calcular, elas saem do projeto (a grade recria a sua
  // no proximo render) e so entao o calculo comeca — dai o disparo adiado.
  const pendingStartRef = useRef(false);

  const requestCalculate = useCallback((): void => {
    // No celular a coluna de entradas e uma GAVETA por cima do resultado:
    // deixa-la aberta esconderia justamente o plano de corte que o usuario
    // acabou de mandar calcular. Na tela larga as duas coisas convivem, e
    // fechar seria perder o lugar onde ele estava digitando.
    if (isDrawerLayout()) setSidebarOpen(false);

    const blankParts = project.parts.filter(isBlankPartRow).map((p) => p.id);
    const blankStock = project.stock.filter(isBlankStockRow).map((s) => s.id);
    const dropParts = blankParts.length > 0 && blankParts.length < project.parts.length;
    const dropStock = blankStock.length > 0 && blankStock.length < project.stock.length;

    if (dropParts) dispatch({ type: 'removeParts', ids: blankParts });
    if (dropStock) dispatch({ type: 'removeStock', ids: blankStock });

    if (dropParts || dropStock) {
      pendingStartRef.current = true;
      return;
    }
    optimizer.start();
  }, [project, dispatch, optimizer]);

  // Dispara o calculo assim que o projeto limpo chega.
  useEffect(() => {
    if (!pendingStartRef.current) return;
    pendingStartRef.current = false;
    optimizer.start();
  }, [project, optimizer]);

  // --- acoes de projeto ------------------------------------------------------
  const handleNewProject = useCallback((): void => {
    dispatch({ type: 'newProject' });
    optimizer.clear();
    notify('info', 'Projeto novo criado. Use Ctrl+Z para voltar ao anterior.');
  }, [dispatch, optimizer, notify]);

  const handleLoadSample = useCallback((): void => {
    dispatch({ type: 'loadSample' });
    optimizer.clear();
    notify('info', 'Exemplo carregado. Clique em CALCULAR para ver o plano de corte.');
  }, [dispatch, optimizer, notify]);

  const handleRename = useCallback(
    (name: string): void => dispatch({ type: 'setName', name }),
    [dispatch],
  );

  const openProjectPicker = useCallback((): void => {
    projectInputRef.current?.click();
  }, []);

  const handleProjectFile = useCallback(
    (file: File): void => {
      readFileAsText(file).then(
        (text) => {
          const { project: loaded, error } = importProjectJson(text);
          if (loaded === null) {
            notify('danger', error ?? 'Não foi possível ler este arquivo de projeto.');
            return;
          }
          dispatch({ type: 'setProject', project: loaded });
          optimizer.clear();
          notify('success', `Projeto "${loaded.name}" aberto.`);
        },
        () => notify('danger', 'Não foi possível ler o arquivo escolhido.'),
      );
    },
    [dispatch, optimizer, notify],
  );

  const handleImportCsv = useCallback((target: CsvImportTarget): void => {
    csvTargetRef.current = target;
    csvInputRef.current?.click();
  }, []);

  const handleCsvFile = useCallback(
    (file: File): void => {
      const target = csvTargetRef.current;
      readFileAsText(file).then(
        (text) => {
          if (target === 'parts') {
            const { parts, errors } = parsePartsCsv(text, project.settings);
            if (parts.length === 0) {
              notify('danger', errors[0] ?? 'Nenhuma peça foi reconhecida neste arquivo.');
              return;
            }
            dispatch({ type: 'addParts', parts });
            notify('success', `${parts.length} peça(s) importada(s) de ${file.name}.`);
            return;
          }
          const { stock, errors } = parseStockCsv(text, project.settings);
          if (stock.length === 0) {
            notify('danger', errors[0] ?? 'Nenhuma chapa foi reconhecida neste arquivo.');
            return;
          }
          dispatch({ type: 'addStockSheets', sheets: stock });
          notify('success', `${stock.length} chapa(s) importada(s) de ${file.name}.`);
        },
        () => notify('danger', 'Não foi possível ler o arquivo escolhido.'),
      );
    },
    [dispatch, notify, project.settings],
  );

  const handleExportJson = useCallback((): void => {
    downloadProject(project);
    notify('success', 'Arquivo do projeto gerado.');
  }, [project, notify]);

  const handleExportCsv = useCallback(
    (target: CsvTarget): void => {
      const base = slugifyFilename(project.name);
      const result = optimizer.result;
      if (target === 'parts') {
        downloadText(`${base}-pecas.csv`, partsToCsv(project.parts, project.settings));
      } else if (target === 'stock') {
        downloadText(`${base}-chapas.csv`, stockToCsv(project.stock, project.settings));
      } else if (result === null) {
        notify('danger', 'Calcule o plano de corte antes de exportar esta planilha.');
        return;
      } else if (target === 'cuts') {
        downloadText(`${base}-cortes.csv`, cutsToCsv(result, project.settings));
      } else {
        downloadText(`${base}-sobras.csv`, offcutsToCsv(result, project.settings));
      }
      notify('success', 'Planilha CSV gerada.');
    },
    [project, optimizer.result, notify],
  );

  const handleShare = useCallback((): void => {
    const link = createShareLink(project);
    setShare({ url: link.url, error: link.error });
    setShareOpen(true);
  }, [project]);

  const handlePrint = useCallback((): void => {
    if (optimizer.result === null) {
      notify('danger', 'Calcule o plano de corte antes de imprimir.');
      return;
    }
    printResult();
  }, [optimizer.result, notify]);

  const handleUndo = useCallback((): void => dispatch({ type: 'undo' }), [dispatch]);
  const handleRedo = useCallback((): void => dispatch({ type: 'redo' }), [dispatch]);

  // --- fita de borda ---------------------------------------------------------
  const bandingPart: Part | null = useMemo(() => {
    if (bandingPartId === null) return null;
    return project.parts.find((p) => p.id === bandingPartId) ?? null;
  }, [bandingPartId, project.parts]);

  const openEdgeBandingFor = useCallback((partId: Id): void => {
    setBandingPartId(partId);
    setEdgeOpen(true);
  }, []);

  const openEdgeBands = useCallback((): void => {
    setBandingPartId(null);
    setEdgeOpen(true);
  }, []);

  const closeEdgeBanding = useCallback((): void => {
    setEdgeOpen(false);
    setBandingPartId(null);
  }, []);

  // --- sidebar ---------------------------------------------------------------
  const toggleSidebar = useCallback((): void => setSidebarOpen((v) => !v), []);
  const closeSidebar = useCallback((): void => setSidebarOpen(false), []);
  const handleSidebarResize = useCallback(
    (w: number): void => updatePrefs({ sidebarWidth: w }),
    [updatePrefs],
  );

  const anyModalOpen = settingsOpen || edgeOpen || shortcutsOpen || shareOpen;

  // --- atalhos globais -------------------------------------------------------
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      // Quem estiver mais perto do usuario ja tratou (grade, modal, campo).
      if (e.defaultPrevented) return;

      const target = e.target as HTMLElement | null;
      const tag = target?.tagName ?? '';
      const typing =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        target?.isContentEditable === true;
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();

      if (mod && e.key === 'Enter') {
        e.preventDefault();
        requestCalculate();
        return;
      }
      if (mod && key === 's') {
        e.preventDefault();
        handleExportJson();
        return;
      }
      if (mod && key === 'o') {
        e.preventDefault();
        openProjectPicker();
        return;
      }
      if (mod && key === 'b') {
        e.preventDefault();
        toggleSidebar();
        return;
      }
      if (mod && !typing && key === 'z') {
        e.preventDefault();
        if (e.shiftKey) handleRedo();
        else handleUndo();
        return;
      }
      if (mod && !typing && key === 'y') {
        e.preventDefault();
        handleRedo();
        return;
      }
      if (!mod && !typing && e.key === '?') {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }
      if (e.key === 'Escape' && !anyModalOpen && optimizer.status === 'running') {
        e.preventDefault();
        optimizer.cancel();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    optimizer,
    requestCalculate,
    handleExportJson,
    handleUndo,
    handleRedo,
    openProjectPicker,
    toggleSidebar,
    anyModalOpen,
  ]);

  // --- estilo do grid (largura da coluna de entradas) ------------------------
  const shellStyle = useMemo<CSSProperties>(() => {
    const width = sidebarOpen ? prefs.sidebarWidth : 0;
    return { ['--sidebar-w' as string]: `${width}px` } as CSSProperties;
  }, [sidebarOpen, prefs.sidebarWidth]);

  return (
    <div className={sidebarOpen ? 'app-shell is-sidebar-open' : 'app-shell'} style={shellStyle}>
      <a className="app-skip" href="#pedra-main">
        Pular para o resultado
      </a>

      <Header
        projectName={project.name}
        onRenameProject={handleRename}
        onNewProject={handleNewProject}
        onLoadSample={handleLoadSample}
        onOpenProject={openProjectPicker}
        onImportCsv={handleImportCsv}
        onExportJson={handleExportJson}
        onExportCsv={handleExportCsv}
        onShare={handleShare}
        onPrint={handlePrint}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenShortcuts={() => setShortcutsOpen(true)}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={canUndo}
        canRedo={canRedo}
        saveStatus={saveStatus}
        theme={prefs.theme}
        onToggleTheme={toggleTheme}
        onToggleSidebar={toggleSidebar}
        sidebarOpen={sidebarOpen}
        errorCount={errorCount}
        hasResult={optimizer.result !== null}
      />

      <Sidebar
        open={sidebarOpen}
        onClose={closeSidebar}
        width={prefs.sidebarWidth}
        onResize={handleSidebarResize}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenEdgeBands={openEdgeBands}
        onOpenEdgeBandingFor={openEdgeBandingFor}
        onCalculate={requestCalculate}
        highlightedPartId={hoveredPartId}
        onHoverPart={setHoveredPartId}
      />

      {sidebarOpen ? (
        <div className="app-backdrop no-print" onClick={closeSidebar} aria-hidden="true" />
      ) : null}

      <main className="app-main" id="pedra-main" tabIndex={-1}>
        <ResultsPanel
          result={optimizer.result}
          settings={project.settings}
          edgeBands={project.edgeBands}
          status={optimizer.status}
          progress={optimizer.progress}
          error={optimizer.error}
          stale={optimizer.stale}
          onCalculate={requestCalculate}
          onCancel={optimizer.cancel}
          onLoadSample={handleLoadSample}
          projectName={project.name}
          onNotify={notify}
        />
      </main>

      {/* Seletores de arquivo, escondidos: acionados pelo cabecalho e pelos atalhos. */}
      <input
        ref={projectInputRef}
        type="file"
        accept=".json,application/json"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) handleProjectFile(file);
        }}
      />
      <input
        ref={csvInputRef}
        type="file"
        accept=".csv,.txt,text/csv,text/plain"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) handleCsvFile(file);
        }}
      />

      <SettingsModal
        open={settingsOpen}
        settings={project.settings}
        onChange={(patch) => dispatch({ type: 'updateSettings', patch })}
        onReset={() => dispatch({ type: 'resetSettings' })}
        onClose={() => setSettingsOpen(false)}
        theme={prefs.theme}
        onToggleTheme={toggleTheme}
      />

      <EdgeBandingModal
        open={edgeOpen}
        part={bandingPart}
        edgeBands={project.edgeBands}
        settings={project.settings}
        onChangePart={(id, patch) => dispatch({ type: 'updatePart', id, patch })}
        onAddBand={() => dispatch({ type: 'addEdgeBand' })}
        onUpdateBand={(id, patch) => dispatch({ type: 'updateEdgeBand', id, patch })}
        onRemoveBand={(id) => dispatch({ type: 'removeEdgeBand', id })}
        onClose={closeEdgeBanding}
      />

      <ShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      <ShareModal
        open={shareOpen}
        share={share}
        onClose={() => setShareOpen(false)}
        onCopied={(ok) =>
          notify(
            ok ? 'success' : 'danger',
            ok
              ? 'Link copiado para a área de transferência.'
              : 'Não foi possível copiar automaticamente. Selecione o texto e use Ctrl+C.',
          )
        }
      />

      <div className="toast-region no-print" role="status" aria-live="polite">
        {toast !== null ? (
          <div className={`toast toast--${toast.tone}`}>
            <span className="toast__text">{toast.text}</span>
            <button
              type="button"
              className="icon-btn toast__close"
              onClick={() => setToast(null)}
              title="Fechar aviso"
            >
              <span aria-hidden="true">✕</span>
              <span className="sr-only">Fechar aviso</span>
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// RAIZ
// -----------------------------------------------------------------------------

export function App(): ReactElement {
  return (
    <ProjectProvider>
      <OptimizerProvider>
        <AppShell />
      </OptimizerProvider>
    </ProjectProvider>
  );
}

export default App;
